/**
 * Theme Toggle Component
 * Handles light, sepia, and dark theme switching with localStorage persistence.
 */
export default class ThemeToggle {
    constructor() {
        this.toggles = document.querySelectorAll('[data-theme-toggle]');
        this.cycleButtons = document.querySelectorAll('[data-theme-cycle]');
        if (!this.toggles.length && !this.cycleButtons.length) return;

        this.themes = ['light', 'sepia', 'dark'];
        this.storageKey = 'theme';
        this.init();
    }

    init() {
        const savedTheme = this.getSavedTheme();
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        const theme = this.isValidTheme(savedTheme) ? savedTheme : (prefersDark ? 'dark' : 'light');

        this.setTheme(theme, false);

        this.toggles.forEach(toggle => {
            toggle.addEventListener('click', (event) => {
                const option = event.target.closest('[data-theme-option]');
                if (!option || !toggle.contains(option)) return;

                this.setTheme(option.dataset.themeOption);
            });
        });

        this.cycleButtons.forEach(button => {
            button.addEventListener('click', () => {
                const current = document.documentElement.dataset.theme;
                const next = this.themes[(this.themes.indexOf(current) + 1) % this.themes.length];
                this.setTheme(next);
            });
        });

        window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
            if (!this.getSavedTheme()) {
                this.setTheme(e.matches ? 'dark' : 'light', false);
            }
        });
    }

    isValidTheme(theme) {
        return this.themes.includes(theme);
    }

    getSavedTheme() {
        try {
            return window.localStorage.getItem(this.storageKey);
        } catch (_) {
            return null;
        }
    }

    setTheme(theme, shouldSave = true) {
        if (!this.isValidTheme(theme)) return;

        document.documentElement.dataset.theme = theme;

        if (shouldSave) {
            try {
                window.localStorage.setItem(this.storageKey, theme);
            } catch (_) { }
        }

        this.updateToggles(theme);
    }

    updateToggles(theme) {
        this.cycleButtons.forEach(button => {
            const next = this.themes[(this.themes.indexOf(theme) + 1) % this.themes.length];
            button.dataset.currentTheme = theme;
            button.setAttribute('title', button.dataset[next] || next);
            button.setAttribute('aria-label', button.dataset[next] || next);
        });
        this.toggles.forEach(toggle => {
            toggle.dataset.currentTheme = theme;

            toggle.querySelectorAll('[data-theme-option]').forEach(option => {
                const isActive = option.dataset.themeOption === theme;
                option.setAttribute('aria-pressed', isActive ? 'true' : 'false');
            });
        });
    }
}
